"use client";

import React from "react";

import { Button } from "@/components/ui/button";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { Copy, Mail, PhoneCall } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Props = {
  name: string;
  skipHeadline?: boolean;
  contact: {
    phoneNumbers: Array<string>;
    emails: Array<string>;
    websiteUrl: string | null;
  };
};

const OwnerHeaderContactList: React.FC<Props> = ({
  contact,
  name,
  skipHeadline = false,
}) => {
  const t = useTranslations("owner.contact");

  const hasPhoneNumbers = contact.phoneNumbers.length > 0;
  const hasEmails = contact.emails.length > 0;

  const onCopyText = (text: string, type: "url" | "phone" | "email") => {
    navigator.clipboard.writeText(text);

    toast.success(t(`copy-success.${type}`));
  };

  const callPhoneNumber = (phone: string) => {
    window.open(`tel:${phone}`);
  };

  const openEmailClient = (email: string) => {
    window.open(`mailto:${email}`);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {!skipHeadline && (
        <p className="text-base font-semibold">{t("direct-methods")}</p>
      )}
      {hasPhoneNumbers && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-600">{t("phones")}</p>
          <div className="grid grid-cols-1">
            {contact.phoneNumbers.map((phone) => (
              <div
                key={phone}
                className="flex min-w-0 flex-row gap-2 px-2 py-1 items-center justify-between border border-input rounded-xl "
              >
                <p className="min-w-0 break-all text-sm">{phone}</p>

                <div className="flex shrink-0 flex-row gap-2 items-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          aria-label={t("copy.phone")}
                          type="button"
                          variant="secondary"
                          size="icon-sm"
                          onClick={() => onCopyText(phone, "phone")}
                        >
                          <Copy />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-60 text-center">
                        <p className="min-w-0 break-all text-sm">
                          {t(`copy.phone`)}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          aria-label={t("call", { name })}
                          type="button"
                          variant="secondary"
                          size="icon-sm"
                          onClick={() => callPhoneNumber(phone)}
                        >
                          <PhoneCall />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-60 text-center">
                        <p className="min-w-0 break-all text-sm">
                          {t(`call`, { name })}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {hasEmails && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-600">{t("emails")}</p>
          <div className="grid grid-cols-1">
            {contact.emails?.map((email) => (
              <div
                key={email}
                className="flex min-w-0 flex-row gap-2 px-2 py-1 items-center justify-between border border-input rounded-xl "
              >
                <p className="min-w-0 break-all text-sm">{email}</p>

                <div className="flex shrink-0 flex-row gap-2 items-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          aria-label={t("copy.email")}
                          type="button"
                          variant="secondary"
                          size="icon-sm"
                          onClick={() => onCopyText(email, "email")}
                        >
                          <Copy />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-60 text-center">
                        <p className="min-w-0 break-all text-sm">
                          {t(`copy.email`)}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          aria-label={t("email", { name })}
                          type="button"
                          variant="secondary"
                          size="icon-sm"
                          onClick={() => openEmailClient(email)}
                        >
                          <Mail />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-60 text-center">
                        <p className="min-w-0 break-all text-sm">
                          {t(`email`, { name })}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerHeaderContactList;
